import { defineStore } from 'pinia'
import type { CorporateAccountUserProfile } from '@/models/api/corporate-account-user-profile'
import {
  basicUserProfileSchema,
  userProfileSchema,
  type BasicUserProfile,
  type UserProfile
} from '@/models/api/user-profile'
import { api } from '@/services'
import { isLoading } from '@/utils/functions/is-loading'
import { useStorage } from '~library/composables'
import { Identity, LocalStorage } from '~library/enums'
import type { CorporateAccount } from '~library/models'
import { isApiSuccess, isType } from '~library/utils/functions'

type UserProfileData =
  | UserProfile
  | CorporateAccountUserProfile
  | BasicUserProfile
  | null

export const useUserStore = defineStore('user', () => {
  const _userProfile = ref<UserProfileData>(null)

  /** 企業帳號 - 登入者帳號 */
  const _corporateAccount = ref<string | null>(null)

  const _corporateAccountData = ref<CorporateAccount | null>(null)

  const { setStorageData, removeStorageData } = useStorage()

  const bizId = computed((): string | undefined => _userProfile.value?.bizId)

  const userProfile = computed((): UserProfileData => _userProfile.value)

  const corporateAccountData = computed(
    (): CorporateAccount | null => _corporateAccountData.value
  )

  const corporateAccount = computed((): string | null => {
    return _corporateAccount.value
  })

  const memberCardNumber = computed((): string | undefined =>
    isType(userProfile.value, userProfileSchema) ||
    isType(userProfile.value, basicUserProfileSchema)
      ? userProfile.value.memberCardNumber
      : undefined
  )

  const isCorporateAccount = computed(() => {
    return isCorporateAccountTypeGuide(userProfile.value)
  })

  function isCorporateAccountTypeGuide(
    data: UserProfileData
  ): data is CorporateAccountUserProfile {
    return !!(
      data?.identity &&
      [Identity.CORPORATE_ACCOUNT, Identity.CORPORATE_SUB_ACCOUNT].includes(
        data.identity
      )
    )
  }

  async function setUserProfile(data: UserProfileData) {
    _userProfile.value = data

    if (data) {
      setStorageData(LocalStorage.USER_PROFILE, data)
    } else {
      removeStorageData(LocalStorage.USER_PROFILE)
    }

    await setCorporateAccountByUserProfile()
  }

  function setCorporateAccountData(value: CorporateAccount | null) {
    _corporateAccountData.value = value
  }

  function setCorporateAccount(value: string | null) {
    _corporateAccount.value = value

    if (value) {
      setStorageData(LocalStorage.CORPORATE_ACCOUNT, value)
    } else {
      removeStorageData(LocalStorage.CORPORATE_ACCOUNT)
    }
  }

  async function setCorporateAccountByUserProfile() {
    if (!isCorporateAccount.value) {
      setCorporateAccountData(null)
      return
    }
    const response = await isLoading(
      api.myAccount.getAdministratorInformation()
    )

    if (!isApiSuccess(response)) {
      setCorporateAccountData(null)
      return
    }

    if (!corporateAccount.value) {
      setCorporateAccountData(null)
      return
    }

    setCorporateAccountData(
      await getCorporateAccountData(corporateAccount.value)
    )
  }

  async function updateCorporateAccountData() {
    if (!corporateAccount.value) {
      return
    }

    setCorporateAccountData(
      await getCorporateAccountData(corporateAccount.value)
    )
  }

  async function getCorporateAccountData(
    account: string
  ): Promise<CorporateAccount | null> {
    const response = await isLoading(api.corporateAccount.getData(account))

    if (!isApiSuccess(response)) {
      return null
    }

    return response.data
  }

  /**
   * 清空使用者登入資訊
   */
  function clearUserProfile() {
    _userProfile.value = null
    _corporateAccount.value = null

    removeStorageData(LocalStorage.USER_PROFILE)
    removeStorageData(LocalStorage.CORPORATE_ACCOUNT)
  }

  return {
    bizId,
    isCorporateAccount,
    isCorporateAccountTypeGuide,
    corporateAccount,
    memberCardNumber,
    userProfile,
    setCorporateAccount,
    setUserProfile,
    clearUserProfile,
    corporateAccountData,
    updateCorporateAccountData
  }
})
